import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { EXTERNAL } from '@/constants/brand';
import { PV } from '@/constants/testIds';
import { Menu, X, ArrowUpRight, Instagram, Facebook } from 'lucide-react';
import { setLeadSource } from '@/lib/leadSource';
import { EVENTS_PORTAL_URL } from '@/lib/eventsPortal';

// Nav items use REAL routes (not anchor scroll links).
// Order: HOME → Chi siamo → Perché sceglierci → A chi ci rivolgiamo → FAQ → Contatti.
// "Eventi" opens the unified external events portal.
const NAV_ITEMS = [
  { to: '/', key: 'home', testId: 'pv-header-nav-home', exact: true },
  { to: '/chi-siamo', key: 'about', testId: 'pv-header-nav-about' },
  { to: '/perche-sceglierci', key: 'why', testId: PV.headerNavWhy },
  { to: '/a-chi-ci-rivolgiamo', key: 'target', testId: PV.headerNavTarget },
  { to: '/faq', key: 'faq', testId: 'pv-header-nav-faq' },
  { href: EVENTS_PORTAL_URL, key: 'eventi', testId: 'pv-header-nav-eventi', external: true },
  { to: '/contatti', key: 'contact', testId: PV.headerNavContact },
];

const navLabel = (t, key) => {
  if (key === 'faq') return 'FAQ';
  return t.nav[key];
};

export const Header = () => {
  const { t, locale, setLocale } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.10)]'
          : 'bg-white/0 border-b border-transparent'
      }`}
    >
      <div className="pv-container flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid={PV.headerLogo}
          aria-label="Prime Vision"
        >
          <img
            src="/assets/prime-vision-logo.png"
            alt="Prime Vision"
            className="h-7 sm:h-8 md:h-9 w-auto object-contain"
            draggable={false}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 mx-5 xl:mx-8 text-[13.5px] xl:text-[14px] font-semibold text-slate-700">
          {NAV_ITEMS.map((item) => (
            item.external ? (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={item.testId}
                className="whitespace-nowrap transition-colors hover:text-pv-teal"
              >
                {navLabel(t, item.key)}
              </a>
            ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              data-testid={item.testId}
              className={({ isActive }) =>
                `whitespace-nowrap transition-colors ${
                  isActive ? 'text-pv-teal' : 'hover:text-pv-teal'
                }`
              }
            >
              {navLabel(t, item.key)}
            </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-2.5">
          {/* Social icons — only on 2xl+ to keep nav clean */}
          <div className="hidden 2xl:flex items-center gap-1.5 mr-1">
            <a
              href={EXTERNAL.INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram Prime Vision"
              data-testid="pv-header-instagram"
              className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-pv-orange/40 hover:bg-slate-50 flex items-center justify-center text-slate-600 hover:text-pv-orange transition-colors"
            >
              <Instagram className="w-3.5 h-3.5" />
            </a>
            <a
              href={EXTERNAL.FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook Prime Vision"
              data-testid="pv-header-facebook"
              className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-[#1877F2]/40 hover:bg-slate-50 flex items-center justify-center text-slate-600 hover:text-[#1877F2] transition-colors"
            >
              <Facebook className="w-3.5 h-3.5" fill="currentColor" />
            </a>
            <a
              href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20avere%20maggiori%20informazioni%20su%20Prime%20Vision."
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp Prime Vision · +39 352 052 6856"
              data-testid="pv-header-whatsapp"
              className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-[#25D366]/50 hover:bg-[#25D366]/5 flex items-center justify-center text-slate-600 hover:text-[#25D366] transition-colors"
              title="Scrivici su WhatsApp · +39 352 052 6856"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03ZM12.05 20.13h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 1 1 6.99 3.86Zm4.51-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23a7.49 7.49 0 0 1-1.38-1.72c-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.07-.1-.23-.16-.48-.29Z"/>
              </svg>
            </a>
          </div>

          <div className="hidden md:flex items-center bg-slate-100 border border-slate-200 rounded-full p-1 mr-1">
            <button
              data-testid={PV.headerLangIt}
              onClick={() => setLocale('it')}
              className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full transition-all ${
                locale === 'it' ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'text-slate-500 hover:text-slate-900'
              }`}
            >IT</button>
            <button
              data-testid={PV.headerLangEn}
              onClick={() => setLocale('en')}
              className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full transition-all ${
                locale === 'en' ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'text-slate-500 hover:text-slate-900'
              }`}
            >EN</button>
          </div>

          <a
            href={EXTERNAL.AUTH_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={PV.headerLoginBtn}
            className="hidden md:inline-flex pv-btn-ghost pv-btn-sm gap-1.5"
          >
            {t.nav.login}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>

          <Link
            to="/bookacall"
            data-testid={PV.headerDemoBtn}
            onClick={() => setLeadSource('header-demo')}
            className="hidden md:inline-flex pv-btn-primary pv-btn-sm"
          >
            {t.nav.demo}
          </Link>

          <button
            data-testid={PV.headerMobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl">
          <div className="pv-container py-6 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              item.external ? (
                <a
                  key={item.key}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${item.testId}-mobile`}
                  onClick={() => setOpen(false)}
                  className="text-base font-semibold py-3 border-b border-slate-100 transition-colors text-slate-700 hover:text-pv-teal"
                >
                  {navLabel(t, item.key)}
                </a>
              ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={`${item.testId}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base font-semibold py-3 border-b border-slate-100 transition-colors ${
                    isActive ? 'text-pv-teal' : 'text-slate-700 hover:text-pv-teal'
                  }`
                }
              >
                {navLabel(t, item.key)}
              </NavLink>
              )
            ))}

            {/* Mobile primary CTA — demo */}
            <Link
              to="/bookacall"
              onClick={() => { setLeadSource('header-mobile-demo'); setOpen(false); }}
              data-testid="pv-header-mobile-demo-btn"
              className="pv-btn-primary pv-btn-block mt-4 justify-center"
            >
              {t.nav.demo}
            </Link>

            <a
              href={EXTERNAL.AUTH_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pv-header-mobile-login-btn"
              className="pv-btn-ghost pv-btn-sm pv-btn-block mt-2 gap-1.5"
            >
              {t.nav.login}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>

            <div className="flex items-center gap-3 pt-5 flex-wrap">
              <button
                onClick={() => setLocale('it')}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full transition-all ${
                  locale === 'it' ? 'bg-pv-teal text-white' : 'text-slate-500 bg-slate-100'
                }`}
              >IT</button>
              <button
                onClick={() => setLocale('en')}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full transition-all ${
                  locale === 'en' ? 'bg-pv-teal text-white' : 'text-slate-500 bg-slate-100'
                }`}
              >EN</button>
              <a
                href={EXTERNAL.INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:text-pv-orange hover:border-pv-orange/40 transition-colors"
              >
                <Instagram className="w-3.5 h-3.5" />
              </a>
              <a
                href={EXTERNAL.FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#1877F2] hover:border-[#1877F2]/40 transition-colors"
              >
                <Facebook className="w-3.5 h-3.5" fill="currentColor" />
              </a>
              <a
                href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20avere%20maggiori%20informazioni%20su%20Prime%20Vision."
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Prime Vision"
                className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#25D366] hover:border-[#25D366]/50 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                  <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03ZM12.05 20.13h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 1 1 6.99 3.86Zm4.51-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23a7.49 7.49 0 0 1-1.38-1.72c-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.07-.1-.23-.16-.48-.29Z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
